import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Layers, Loader2, ChevronRight, BookOpen, ArrowRight } from "lucide-react";
import { logEvent } from "@/lib/events";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearnerLearningExperiences() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/learner/dashboard`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    logEvent("learning_experiences_viewed");
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const tracks = stats?.tracks || [];

  return (
    <div className="space-y-6" data-testid="learner-learning-experiences">
      <div>
        <h1 className="font-heading text-2xl font-bold text-gray-900">Learning Experiences</h1>
        <p className="text-sm text-gray-500 font-body mt-1">
          Explore the tracks and learning experiences assigned to your cohort.
        </p>
      </div>

      {tracks.length === 0 ? (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <Layers className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 font-body">No tracks have been assigned yet.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="le-tracks-list">
          {tracks.map((t) => {
            const completed = t.completed_items || t.completed_lexps || 0;
            const total = t.total_items || t.total_lexps || 0;
            const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
            const isComplete = total > 0 && completed >= total;
            const hasStarted = (t.has_started || completed > 0) && !isComplete;

            // Inline CTA — mirrors the dashboard TrackCard so learners see
            // the same "Begin / Continue / Review" affordance in both
            // places. Clicking the row and clicking the button lead to
            // the same track detail page.
            let ctaLabel = (
              <>
                Begin <ArrowRight className="h-3.5 w-3.5" />
              </>
            );
            let ctaClass =
              "bg-emerald-500 hover:bg-emerald-600 text-white";
            let ctaStyle;
            if (isComplete || t.submitted) {
              ctaLabel = "Review";
              ctaClass = "bg-gray-100 hover:bg-gray-200 text-gray-700";
            } else if (hasStarted) {
              ctaLabel = (
                <>
                  Continue <ArrowRight className="h-3.5 w-3.5" />
                </>
              );
              ctaClass = "text-white hover:opacity-90";
              ctaStyle = { backgroundColor: "#4A90D9" };
            }

            return (
              <div
                key={t.id}
                onClick={() => navigate(`/portal/tracks/${t.id}`)}
                data-testid={`le-track-${t.id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/portal/tracks/${t.id}`);
                  }
                }}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md hover:border-gray-300 transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Layers className="h-4 w-4 shrink-0" style={{ color: "var(--theme-primary, var(--primary-color, #4A90D9))" }} />
                      <h3 className="text-base font-body font-bold text-gray-900 truncate">{t.title}</h3>
                    </div>
                    {t.description && (
                      <p className="text-sm text-gray-500 font-body line-clamp-2 ml-6 mb-2">{t.description}</p>
                    )}
                    <div className="ml-6 space-y-1.5">
                      <div className="flex items-center justify-between text-xs font-body max-w-sm">
                        <span className="text-gray-500">
                          <BookOpen className="h-3 w-3 inline mr-1" />
                          {completed} of {total} complete
                        </span>
                        <span className="text-gray-700 font-semibold">{pct}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden max-w-sm">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: pct === 100 ? "#10B981" : "var(--primary-color, #4A90D9)",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 mt-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        // Prevent the row-level onClick from double-firing
                        // (the row and the button both navigate to the
                        // same destination, but we still want a clean
                        // single-navigation event and no accidental
                        // interference from parent hover styling).
                        e.stopPropagation();
                        navigate(`/portal/tracks/${t.id}`);
                      }}
                      style={ctaStyle}
                      className={`inline-flex items-center gap-1 h-9 px-4 rounded-md text-xs font-body font-semibold transition-colors ${ctaClass}`}
                      data-testid={`le-track-cta-${t.id}`}
                    >
                      {ctaLabel}
                    </button>
                    <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-gray-500" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
